import {
  assertNever,
  BookTimeZone,
  CallSheetSort,
  ProspectBookFilter,
  ProspectBookSort,
  SortDirection,
  type ProspectBookActivity,
  type ProspectBookMatch,
  type ProspectBookQuery,
} from '@signalvote/shared';

/**
 * The Prospects book's filters as the URL carries them — `?activity=&match=&sheet=&q=`, plus an uploaded
 * list, a place, a time zone and an Ask range (`&list=&state=&city=&zip=&timeZone=&minAsk=&maxAsk=`) — so opening a person,
 * stepping with J/K and coming back all keep them. Unset means any.
 */
export interface BookFilters {
  activity?: ProspectBookActivity;
  match?: ProspectBookMatch;
  sheet?: string;
  q?: string;
  list?: string;
  state?: string;
  city?: string;
  zip?: string;
  /** Inclusive Ask range ends, as the URL writes them. */
  minAsk?: string;
  maxAsk?: string;
  timeZone?: BookTimeZone;
  /** The column the server sorts by; unset is largest Ask first. */
  sort?: ProspectBookSort;
  /** Which way; unset with a column is ascending. */
  dir?: SortDirection;
}

const SORTS: readonly string[] = Object.values(ProspectBookSort);
const DIRECTIONS: readonly string[] = Object.values(SortDirection);

/** The book's time zones, in the order the filter offers them — east to west, then unknown. */
export const BOOK_TIME_ZONES: readonly BookTimeZone[] = [
  BookTimeZone.Eastern,
  BookTimeZone.Central,
  BookTimeZone.Mountain,
  BookTimeZone.Pacific,
  BookTimeZone.Alaska,
  BookTimeZone.Hawaii,
  BookTimeZone.Unknown,
];

/** A time zone's name in the filter. */
export function timeZoneLabel(zone: BookTimeZone): string {
  switch (zone) {
    case BookTimeZone.Eastern:
      return 'Eastern';
    case BookTimeZone.Central:
      return 'Central';
    case BookTimeZone.Mountain:
      return 'Mountain';
    case BookTimeZone.Pacific:
      return 'Pacific';
    case BookTimeZone.Alaska:
      return 'Alaska';
    case BookTimeZone.Hawaii:
      return 'Hawaii';
    case BookTimeZone.Unknown:
      return 'Unknown';
    default:
      return assertNever(zone);
  }
}

const ACTIVITY: readonly string[] = [ProspectBookFilter.NeverCalled, ProspectBookFilter.CallBackOwed, ProspectBookFilter.OpenCommitment];
const MATCH: readonly string[] = [
  ProspectBookFilter.FederalGiving,
  ProspectBookFilter.PossibleMatch,
  ProspectBookFilter.NoneFound,
  ProspectBookFilter.NotMatchedYet,
  ProspectBookFilter.NoPhone,
];

const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : undefined);

/** An Ask end the URL can carry: a non-negative amount. */
const amount = (value: unknown) => {
  const raw = typeof value === 'number' ? String(value) : text(value);
  return raw !== undefined && /^\d+(\.\d+)?$/.test(raw) ? raw : undefined;
};

/** A route's `validateSearch`: keeps each filter only when it's one that filter takes. */
export function parseBookFilters(search: Record<string, unknown>): BookFilters {
  const filters: BookFilters = {};
  const activity = text(search.activity);
  const match = text(search.match);
  const sheet = text(search.sheet);
  const q = text(search.q);
  if (activity && ACTIVITY.includes(activity)) filters.activity = activity as ProspectBookActivity;
  if (match && MATCH.includes(match)) filters.match = match as ProspectBookMatch;
  if (sheet) filters.sheet = sheet;
  if (q) filters.q = q;
  for (const key of ['list', 'state', 'city', 'zip'] as const) {
    const value = text(search[key]);
    if (value) filters[key] = value;
  }
  for (const key of ['minAsk', 'maxAsk'] as const) {
    const value = amount(search[key]);
    if (value) filters[key] = value;
  }
  const timeZone = text(search.timeZone);
  if (timeZone && (BOOK_TIME_ZONES as readonly string[]).includes(timeZone)) filters.timeZone = timeZone as BookTimeZone;
  const sort = text(search.sort);
  const dir = text(search.dir);
  if (sort && SORTS.includes(sort)) filters.sort = sort as ProspectBookSort;
  if (dir && DIRECTIONS.includes(dir)) filters.dir = dir as SortDirection;
  return filters;
}

/**
 * The person whose profile is open over a list — the book or a call book — as `?person=<prospectId>`.
 * It opens and closes with the URL (Back closes it, a shared link reopens it) and is never a filter: the
 * list behind it reads the same whoever is open.
 */
export interface OpenPerson {
  person?: string;
}

/** The book's URL: its filters, and whoever is open over it. */
export type BookSearch = BookFilters & OpenPerson;

/** A call book route's `validateSearch`: just the person open over it. */
export function parseOpenPerson(search: Record<string, unknown>): OpenPerson {
  const person = text(search.person);
  return person ? { person } : {};
}

/**
 * A call book's URL: the person open over it, and an order the Fundraiser is only viewing it in
 * (`?sort=&dir=`) — never saved, so never what Callers get. Unset is the book's saved order.
 */
export interface CallBookSearch extends OpenPerson {
  sort?: CallSheetSort;
  dir?: SortDirection;
}

const CALL_BOOK_SORTS: readonly string[] = Object.values(CallSheetSort);

/** A call book route's `validateSearch`: the person open over it and the order it's viewed in. */
export function parseCallBookSearch(search: Record<string, unknown>): CallBookSearch {
  const parsed: CallBookSearch = parseOpenPerson(search);
  const sort = text(search.sort);
  const dir = text(search.dir);
  if (sort && CALL_BOOK_SORTS.includes(sort)) parsed.sort = sort as CallSheetSort;
  if (dir && DIRECTIONS.includes(dir)) parsed.dir = dir as SortDirection;
  return parsed;
}

/** The book route's `validateSearch`: its filters and the person open over it. */
export function parseBookSearch(search: Record<string, unknown>): BookSearch {
  return { ...parseBookFilters(search), ...parseOpenPerson(search) };
}

/** The book read for the URL's filters. */
export const bookQuery = (filters: BookFilters): Required<ProspectBookQuery> => ({
  activity: filters.activity ?? null,
  match: filters.match ?? null,
  sheetId: filters.sheet ?? null,
  search: filters.q ?? '',
  listId: filters.list ?? null,
  state: filters.state ?? null,
  city: filters.city ?? '',
  zip: filters.zip ?? '',
  minAsk: filters.minAsk ? Number(filters.minAsk) : null,
  maxAsk: filters.maxAsk ? Number(filters.maxAsk) : null,
  timeZone: filters.timeZone ?? null,
  sort: filters.sort ?? null,
  dir: filters.dir ?? null,
});

const wholeDollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** An Ask range in words: "$1,000 and up", "under $1,000", "$1,000–$2,499". An upper end just shy of a
 *  round number (999.99) reads as "under" it. */
export function askRangeLabel(min: number | null, max: number | null): string | null {
  if (min === null && max === null) return null;
  if (max === null) return `${wholeDollars.format(min!)} and up`;
  if (min === null) return `under ${wholeDollars.format(Math.ceil(max))}`;
  return `${wholeDollars.format(min)}–${wholeDollars.format(Math.floor(max))}`;
}

/** The list, place, time zone and Ask filters that are on, in words — for the book's "no one matches" line. */
export function narrowingNames(filters: BookFilters, lists: readonly { id: string; name: string }[]): string[] {
  const { listId, state, city, zip, minAsk, maxAsk, timeZone } = bookQuery(filters);
  const asks = askRangeLabel(minAsk, maxAsk);
  return [
    listId && (lists.find((list) => list.id === listId)?.name ?? 'that list'),
    state,
    city,
    zip && `ZIP ${zip}`,
    timeZone && (timeZone === BookTimeZone.Unknown ? 'time zone unknown' : `${timeZoneLabel(timeZone)} time`),
    asks && `asks ${asks}`,
  ].filter((name): name is string => !!name);
}

/** Every filter's name, for the book's filters, its "no one matches" line and the detail page's caption. */
export function filterLabel(filter: ProspectBookFilter): string {
  switch (filter) {
    case ProspectBookFilter.NeverCalled:
      return 'Never called';
    case ProspectBookFilter.CallBackOwed:
      return 'Call-back';
    case ProspectBookFilter.OpenCommitment:
      return 'Committed';
    case ProspectBookFilter.FederalGiving:
      return 'Federal giving';
    case ProspectBookFilter.PossibleMatch:
      return 'Possible match';
    case ProspectBookFilter.NotMatchedYet:
      return 'Not matched yet';
    case ProspectBookFilter.NoneFound:
      return 'None found';
    case ProspectBookFilter.NoPhone:
      return 'No phone';
    default:
      return assertNever(filter);
  }
}

/** The change that clears the second row — list, place, time zone and Ask — keeping the search, chips and call book. */
export const narrowingChangesCleared = {
  list: null,
  state: null,
  city: null,
  zip: null,
  minAsk: null,
  maxAsk: null,
  timeZone: null,
} as const satisfies { [K in keyof BookFilters]?: null };

/** The filters with some changed; a null or blank one is dropped, so the URL carries only what's on. */
export function withFilters(filters: BookFilters, changes: { [K in keyof BookFilters]?: BookFilters[K] | null }): BookFilters {
  const next: BookFilters = { ...filters };
  for (const key of Object.keys(changes) as (keyof BookFilters)[]) {
    const value = changes[key];
    if (value === null || value === undefined || value === '') delete next[key];
    else (next as Record<string, string>)[key] = value;
  }
  return next;
}
